// ============================================================
// useRecentColours.ts — GLOBAL recently-used text colours.
//
// Recent colours used to be per-RichTextEditor local state, so a colour picked
// in one rich field never showed up in another. This tiny zustand store makes
// them app-wide: every RichTextEditor reads `recent` and calls `pushColour`, so
// a colour applied in ANY rich field immediately appears in the recent row of
// ALL of them (same tab), and the list is persisted to localStorage
// (`casevo_recent_colours`) so it survives reloads. Capped so the row stays tidy.
// ============================================================
import { create } from 'zustand'

const STORAGE_KEY = 'casevo_recent_colours'
const CAP = 8
// The default palette shown before the user has picked anything of their own.
const CLASSIC_COLOURS = ['#000000', '#e11d48', '#2563eb', '#16a34a', '#ea580c', '#9333ea']

const normalise = (c: string): string => c.trim().toLowerCase()

// Best-effort load; any corruption / unavailable storage falls back to the
// classic palette so the picker always has swatches.
const load = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return CLASSIC_COLOURS
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return CLASSIC_COLOURS
    const cleaned = parsed.filter((x): x is string => typeof x === 'string').slice(0, CAP)
    return cleaned.length ? cleaned : CLASSIC_COLOURS
  } catch {
    return CLASSIC_COLOURS
  }
}

const persist = (colours: string[]): void => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(colours)) } catch { /* storage unavailable — in-memory only */ }
}

interface RecentColoursState {
  recent: string[]
  // Move `colour` to the front (dedupe case-insensitively), cap the list, and
  // persist. Called whenever any editor APPLIES a colour.
  pushColour: (colour: string) => void
}

export const useRecentColours = create<RecentColoursState>((set, get) => ({
  recent: load(),
  pushColour: (colour) => {
    const next = [colour, ...get().recent.filter((x) => normalise(x) !== normalise(colour))].slice(0, CAP)
    persist(next)
    set({ recent: next })
  },
}))
